import { useEffect, useMemo, useRef } from 'react';
import type { SlideDoc } from '../types/slide-doc';
import { Stage } from './Stage';
import { ShapeRenderer } from './ShapeRenderer';
import { buildSlideTimeline } from '../runtime/animationRuntime';
import { SlideAudioController } from '../runtime/audioRuntime';
import type { LessonPlayhead } from '../runtime/lessonPlayhead';

interface Props {
  slide: SlideDoc;
  // When `playhead` is provided, animation is driven by the master clock; the
  // GSAP timeline is registered for seek-based scrubbing. When absent, falls
  // back to standalone play (current Phase-1 behaviour).
  playhead?: LessonPlayhead | null;
}

export function SlideView({ slide, playhead }: Props) {
  const refs = useRef(new Map<string, Element>());
  const audioRef = useRef<SlideAudioController | null>(null);

  useMemo(() => {
    refs.current = new Map();
  }, [slide.id]);

  useEffect(() => {
    let killed = false;
    const tl = buildSlideTimeline(slide, refs.current);
    tl.pause();
    const audio = new SlideAudioController();
    audioRef.current = audio;

    const start = async () => {
      if (slide.audio && slide.audio.length) {
        await audio.load(slide.audio);
      }
      if (killed) return;
      if (playhead) {
        playhead.registerSlideTimeline(slide.id, tl);
        // audio cues remain time-based but are stopped on slide unmount; the
        // playhead-driven seek will scrub through them implicitly via GSAP
        // when we refactor cues into the timeline (Phase B nicety).
        audio.start();
      } else {
        tl.play(0);
        audio.start();
      }
    };

    const raf = requestAnimationFrame(() => {
      void start();
    });

    return () => {
      killed = true;
      cancelAnimationFrame(raf);
      if (playhead) playhead.unregisterSlideTimeline(slide.id);
      tl.kill();
      audio.unload();
    };
  }, [slide, playhead]);

  const setRef = (id: string) => (el: Element | null) => {
    if (el) refs.current.set(id, el);
    else refs.current.delete(id);
  };

  return (
    <Stage background={slide.background ?? '#ffffff'}>
      {slide.shapes.map((shape) => (
        <ShapeRenderer key={shape.id} shape={shape} ref={setRef(shape.id) as never} />
      ))}
    </Stage>
  );
}
